
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Modal, Form, InputNumber, Select, message, Space, Tag, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import supplyChainService, { ReplenishmentConfig, SellerSKU } from '../../../services/supplyChainService';

const { Option } = Select;

const ReplenishmentConfigPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState<ReplenishmentConfig[]>([]);
    const [skus, setSkus] = useState<SellerSKU[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchConfigs();
        fetchSkus();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getReplenishmentConfigs();
            setConfigs(res.data);
        } catch (error) {
            message.error('加载配置失败');
        } finally {
            setLoading(false);
        }
    };

    const fetchSkus = async () => {
        try {
            const res = await supplyChainService.getSellerSKUs({ page_size: 1000 });
            setSkus((res.data as any).results || []);
        } catch (error) {
            console.error('Failed to load SKUs');
        }
    };

    const handleEdit = (record: ReplenishmentConfig) => {
        setEditingId(record.id!);
        // Check if seller_sku is object or ID
        const skuId = typeof record.seller_sku === 'object' ? (record.seller_sku as any).id : record.seller_sku;

        form.setFieldsValue({
            seller_sku: skuId,
            target_days_of_inventory: record.target_days_of_inventory,
            safety_stock_days: record.safety_stock_days,
            lead_time_days: record.lead_time_days,
            min_restock_qty: record.min_restock_qty,
        });
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        setEditingId(null);
        form.resetFields();
        // Set defaults
        form.setFieldsValue({
            target_days_of_inventory: 90,
            safety_stock_days: 30,
            lead_time_days: 15,
            min_restock_qty: 100
        });
        setIsModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingId) {
                await supplyChainService.updateReplenishmentConfig(editingId, values);
                message.success('更新成功');
            } else {
                await supplyChainService.createReplenishmentConfig(values);
                message.success('创建成功');
            }
            setIsModalVisible(false);
            fetchConfigs();
        } catch (error) {
            // Validation or API error
            message.error('保存失败');
        }
    };

    const columns = [
        {
            title: 'SKU',
            key: 'sku',
            render: (_: any, record: ReplenishmentConfig) => {
                if (record.seller_sku) {
                    // Handle case where seller_sku might be an ID (number) or nested object
                    const sku = typeof record.seller_sku === 'object' ? record.seller_sku as any : null;
                    if (sku) {
                        return (
                            <div>
                                <b>{sku.sku_code}</b>
                                <div style={{ fontSize: 12, color: '#888' }}>{sku.title || '-'}</div>
                            </div>
                        );
                    }
                    return <Tag color="default">ID: {String(record.seller_sku)}</Tag>;
                }
                return <Tag color="blue">全局默认</Tag>;
            }
        },
        { title: '目标库存天数', dataIndex: 'target_days_of_inventory', key: 'target', render: (val: number) => <Tag color="green">{val}天</Tag> },
        { title: '安全库存天数', dataIndex: 'safety_stock_days', key: 'safety', render: (val: number) => <Tag color="orange">{val}天</Tag> },
        { title: '采购提前期', dataIndex: 'lead_time_days', key: 'lead_time', render: (val: number) => `${val}天` },
        { title: '最小补货量', dataIndex: 'min_restock_qty', key: 'min_qty' },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: ReplenishmentConfig) => (
                <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card title="补货规则配置" extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增规则</Button>
            }>
                <Table
                    dataSource={configs}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            <Modal
                title={editingId ? "编辑规则" : "新增规则"}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="seller_sku" label="适用SKU" help="留空则作为全局默认配置">
                        <Select showSearch placeholder="选择SKU (留空为全局)" allowClear optionFilterProp="children">
                            {skus.map(s => (
                                <Option key={s.id} value={s.id}>{s.sku_code} - {s.title || ''}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="target_days_of_inventory" label="目标库存天数" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={1} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="safety_stock_days" label="安全库存天数" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="lead_time_days" label="采购提前期 (天)" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="min_restock_qty" label="最小补货量" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={1} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default ReplenishmentConfigPage;
